import { useCallback, useEffect, useRef, useState } from "react";

import { ATD_ORDERS_MANUAL_REFRESH_COOLDOWN_MS } from "./ordersRefresh";

export const useOrdersManualRefresh = ({
  refetch,
  isFetching,
  enabled,
}: {
  refetch: () => unknown;
  isFetching: boolean;
  enabled: boolean;
}) => {
  const cooldownUntilRef = useRef(0);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const isFetchingRef = useRef(isFetching);
  const enabledRef = useRef(enabled);
  const [isCoolingDown, setIsCoolingDown] = useState(false);

  isFetchingRef.current = isFetching;
  enabledRef.current = enabled;

  const clearCooldown = useCallback(() => {
    if (timerRef.current !== undefined) {
      clearTimeout(timerRef.current);
      timerRef.current = undefined;
    }
    cooldownUntilRef.current = 0;
    setIsCoolingDown(false);
  }, []);

  useEffect(() => {
    if (!enabled) {
      clearCooldown();
    }
  }, [clearCooldown, enabled]);

  useEffect(
    () => () => {
      if (timerRef.current !== undefined) {
        clearTimeout(timerRef.current);
      }
    },
    [],
  );

  const refresh = useCallback(() => {
    if (!enabledRef.current || isFetchingRef.current) {
      return;
    }
    const now = Date.now();
    if (now < cooldownUntilRef.current) {
      return;
    }

    cooldownUntilRef.current = now + ATD_ORDERS_MANUAL_REFRESH_COOLDOWN_MS;
    setIsCoolingDown(true);
    if (timerRef.current !== undefined) {
      clearTimeout(timerRef.current);
    }
    timerRef.current = setTimeout(() => {
      timerRef.current = undefined;
      cooldownUntilRef.current = 0;
      setIsCoolingDown(false);
    }, ATD_ORDERS_MANUAL_REFRESH_COOLDOWN_MS);
    void refetch();
  }, [refetch]);

  return {
    refresh,
    isRefreshDisabled: !enabled || isFetching || isCoolingDown,
    isRefreshing: isFetching,
  };
};
